// Native-only OTA: swaps the embedded web bundle for the one deployed on the server
// (@capgo/capacitor-updater in manual mode) so UI changes land without a store build.
import { Capacitor } from "@capacitor/core";
// static import: a Capacitor plugin proxy must NEVER be returned through an async function / await
// (the Promise machinery probes `.then` on the proxy → native call that never resolves → hang).
import { CapacitorUpdater } from "@capgo/capacitor-updater";
import { api, BACKEND_URL } from "../api";
import { BUILD_ID } from "../buildInfo";

const RECHECK_MS = 30 * 60 * 1000;
const LAUNCH_GRACE_MS = 20 * 1000; // apply instantly when the app has just started
const startedAt = Date.now();
let timer = null;
let appListener = null;
let pending = null; // downloaded bundle waiting to be applied
let busy = false;
const listeners = new Set();
let status = { state: "idle" }; // idle | checking | downloading | ready | applying | error

const isNative = () => Capacitor.isNativePlatform();

export function subscribeLiveUpdate(fn) {
  listeners.add(fn);
  fn(status);
  return () => listeners.delete(fn);
}
function emit(next) {
  status = next;
  listeners.forEach((fn) => fn(status));
}

const hashOf = (src) => ((src || "").match(/\.([0-9a-f]{8,})\.(?:js|css)(?:\?|$)/) || [])[1] || "";
const mk = (js, css) => (js ? `${js}-${css || "nocss"}` : "");

/** Same formula as backend/live_update.py: `<main.js hash>-<main.css hash>` (sync, DOM based). */
export function localBundleVersion() {
  const js = [...document.scripts].map((s) => s.src).find((s) => /static\/js\/main\./.test(s));
  const css = [...document.querySelectorAll('link[rel="stylesheet"]')].map((l) => l.href).find((h) => /static\/css\/main\./.test(h));
  return mk(hashOf(js), hashOf(css));
}

let resolved = null;
export let localVersionDebug = "";
const withTimeout = (p, ms, label) => Promise.race([p, new Promise((_, rej) => setTimeout(() => rej(new Error(`${label}_timeout`)), ms))]);
const perfVersion = () => {
  try {
    const names = performance.getEntriesByType("resource").map((e) => e.name);
    return mk(hashOf(names.find((n) => /static\/js\/main\./.test(n))), hashOf(names.find((n) => /static\/css\/main\./.test(n))));
  } catch (e) { return ""; }
};
/** Robust version: DOM tags → performance entries → asset-manifest.json (3 s cap, WKWebView may stall on local fetch). */
export async function resolveLocalVersion() {
  if (resolved) return resolved;
  let v = localBundleVersion() || perfVersion();
  if (!v) {
    try {
      const r = await withTimeout(fetch(new URL("asset-manifest.json", document.baseURI).href, { cache: "no-store" }), 3000, "manifest");
      const files = (await withTimeout(r.json(), 2000, "manifest_json"))?.files || {};
      v = mk(hashOf(files["main.js"]), hashOf(files["main.css"]));
      if (!v) localVersionDebug = `manifest:${String(files["main.js"] || "none").slice(-28)}`;
    } catch (e) { localVersionDebug = `manifest_err:${(e?.message || "").slice(0, 30)}`; }
  }
  if (!v) {
    const scripts = [...document.scripts].map((s) => (s.src || "inline").split("/").slice(-2).join("/").slice(0, 28)).join(",").slice(0, 110);
    localVersionDebug = `${localVersionDebug} loc:${(window.location?.href || "").slice(0, 48)} scripts:${scripts}`.trim();
  }
  if (v) resolved = v;
  return v;
}

/** Must run at startup, before any network call — otherwise the plugin rolls back. */
export async function notifyLiveUpdateReady() {
  if (!isNative()) return;
  try {
    const res = await CapacitorUpdater.notifyAppReady();
    const v = res?.bundle?.version;
    if (v && v !== "builtin") setTimeout(async () => report({ state: "running", version: v, mine: await resolveLocalVersion() }), 3000);
  } catch (e) { /* plugin missing in old builds */ }
}

/** For the "Mais" page: which web bundle is running + plugin bundle info. */
export async function getLiveUpdateInfo() {
  const info = { web: await resolveLocalVersion(), debug: localVersionDebug, bundle: null, status };
  if (!isNative()) return info;
  try { info.bundle = (await withTimeout(CapacitorUpdater.current(), 5000, "current"))?.bundle || null; } catch (e) { info.pluginError = e?.message || String(e); }
  return info;
}

async function applyBundle(bundle) {
  emit({ state: "applying", version: bundle.version });
  await CapacitorUpdater.set({ id: bundle.id }); // reloads the WebView into the new bundle
}

export async function checkLiveUpdate({ applyNow } = {}) {
  if (!isNative()) return status;
  if (busy) return { state: "busy" };
  busy = true;
  try {
    emit({ state: "checking" });
    const mine = await resolveLocalVersion();
    const { data } = await api.get("/app/bundle/latest", { params: { t: Date.now() }, timeout: 20000 });
    if (applyNow) report({ state: "checked", version: data?.version, mine: mine || `build:${BUILD_ID}`, reason: mine ? "" : localVersionDebug });
    if (!data?.version) { emit({ state: "idle", reason: "server_no_version" }); return status; }
    // identity: asset hashes when readable, otherwise the compiled BUILD_ID vs the one inside the server's main.js
    const same = mine ? data.version === mine : (!!data.build_id && data.build_id === BUILD_ID);
    if (same) { emit({ state: "idle", version: mine || BUILD_ID, server: mine ? data.version : data.build_id }); return status; }
    if (!mine && !data.build_id) { emit({ state: "idle", server: data.version, reason: `no_local_hash ${localVersionDebug}` }); return status; }

    const { bundles } = await withTimeout(CapacitorUpdater.list(), 10000, "list");
    const known = (bundles || []).find((b) => b.version === data.version);
    if (known?.status === "error") { emit({ state: "error", version: data.version, reason: "rolled_back" }); return status; }

    let bundle = known && known.status === "success" ? known : null;
    if (!bundle) {
      emit({ state: "downloading", version: data.version, size: data.size });
      // v8 of the plugin refuses downloads without a sha256 checksum of the zip
      bundle = await withTimeout(CapacitorUpdater.download({ version: data.version, url: `${BACKEND_URL}${data.url}`, checksum: data.sha256 }), 180000, "download");
    }
    pending = bundle;
    emit({ state: "ready", version: data.version });
    report({ state: "downloaded", version: data.version, mine });
    const justLaunched = Date.now() - startedAt < LAUNCH_GRACE_MS;
    if (applyNow || justLaunched) await applyBundle(bundle);
  } catch (e) {
    const reason = e?.message || String(e);
    emit({ state: "error", reason });
    report({ state: "error", reason, mine: resolved || "" });
  } finally {
    busy = false;
  }
  return status;
}

/** Server-side trail (`/api/app/bundle/events`) so OTA failures on phones can be diagnosed remotely. */
function report(payload) {
  const hasSession = !!(sessionStorage.getItem("pmf_token") || localStorage.getItem("pmf_token"));
  if (!hasSession) return;
  api.post("/app/bundle/event", { platform: Capacitor.getPlatform(), ...payload }).catch(() => {});
}

/** Applies a downloaded bundle now (used by the "Atualizar" action in the yellow bar). */
export async function applyPendingLiveUpdate() {
  if (!pending) return false;
  try { await applyBundle(pending); return true; } catch (e) { emit({ state: "error", reason: e?.message }); return false; }
}

export async function startLiveUpdateWatch() {
  if (!isNative() || timer) return;
  checkLiveUpdate();
  timer = setInterval(() => checkLiveUpdate(), RECHECK_MS);
  try {
    const { App } = await import("@capacitor/app");
    appListener = await App.addListener("appStateChange", async ({ isActive }) => {
      if (isActive) checkLiveUpdate();
      else if (pending) applyPendingLiveUpdate(); // swap while the user is away, like Capgo recommends
    });
  } catch (e) { /* web */ }
}

export function stopLiveUpdateWatch() {
  if (timer) clearInterval(timer);
  timer = null;
  try { appListener && appListener.remove(); } catch (e) {}
  appListener = null;
}
